import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  MenuItem,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Skeleton,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { format } from 'date-fns';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { ProductDetail, formatProductPrice } from '../marketplace/types';
import { BOOKING_THEME } from '../booking/bookingTheme';
import { getProductTypeLabel } from './storeTypes';
import { PHONE_COUNTRY_OPTIONS } from '../booking/countryCodes';

interface CustomerDetails {
  name: string;
  email: string;
  phoneCountryCode: string;
  phone: string;
  notes: string;
}

const stepsFor = (type: ProductDetail['product_type']) =>
  type === 'scheduled_seats'
    ? ['Choose Class', 'Your Details', 'Payment', 'Confirmation']
    : ['Your Details', 'Payment', 'Confirmation'];

const getStorageKey = (ref: string) => `cwy-store-${ref}`;

const buildFullPhone = (code: string, digits: string) => {
  const c = code.startsWith('+') ? code : `+${code}`;
  const d = digits.replace(/\D/g, '').slice(0, 15);
  return d ? `${c}${d}` : c;
};

const generateReference = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `ref-${Date.now()}-${Math.floor(Math.random() * 100000)}`;

export const ProductCheckoutPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { client } = useAuth();

  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkoutStarted, setCheckoutStarted] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedOfferingId, setSelectedOfferingId] = useState('');
  const [customer, setCustomer] = useState<CustomerDetails>({
    name: '',
    email: '',
    phoneCountryCode: '+1',
    phone: '',
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const handledSessionRef = useRef<string | null>(null);

  const stepLabels = useMemo(() => (product ? stepsFor(product.product_type) : []), [product]);

  const selectedOffering = useMemo(
    () => product?.offerings?.find((o) => o.id === selectedOfferingId) ?? null,
    [product, selectedOfferingId]
  );

  const pricing = useMemo(() => {
    if (product?.product_type === 'scheduled_seats') {
      if (selectedOffering) {
        return {
          price: selectedOffering.price,
          tax_amount: selectedOffering.tax_amount,
          total_amount: selectedOffering.total_amount,
          currency: selectedOffering.currency,
        };
      }
    }
    if (product?.product_type === 'flat_fee') {
      return {
        price: product.price || 0,
        tax_amount: product.tax_amount || 0,
        total_amount: product.total_amount || 0,
        currency: product.currency,
      };
    }
    return null;
  }, [product, selectedOffering, selectedOfferingId]);

  // Prefill from auth on mount
  useEffect(() => {
    if (client) {
      setCustomer((prev) => ({
        ...prev,
        name: prev.name || client.name || '',
        email: prev.email || client.email || '',
        phone: prev.phone || (client.phone ? client.phone.replace(/\D/g, '').slice(-10) : ''),
        phoneCountryCode: prev.phoneCountryCode || client.country_code || '+1',
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.client_id]);

  useEffect(() => {
    if (!slug) return;
    const load = async () => {
      try {
        setLoading(true);
        const response = await fetch(API_ENDPOINTS.PRODUCT_BY_SLUG(slug));
        if (!response.ok) throw new Error('Product not found');
        const data = await response.json();
        setProduct(data.product);
      } catch {
        setError('Product not found or unavailable.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  const finalizeRegistration = useCallback(
    async (ref: string, sessionId: string) => {
      setIsSubmitting(true);
      setPaymentMessage('Confirming your payment...');
      setCheckoutStarted(true);

      try {
        // Poll status with retry
        let confirmed = false;
        for (let attempt = 0; attempt < 12; attempt++) {
          if (attempt > 0) await new Promise((r) => setTimeout(r, 3000));
          const statusRes = await fetch(API_ENDPOINTS.STRIPE_STATUS(sessionId));
          const statusData = await statusRes.json();
          if (['confirmed', 'verified', 'succeeded', 'pending'].includes(statusData.transaction?.status)) {
            confirmed = true;
            break;
          }
        }

        if (!confirmed) throw new Error('Payment confirmation timed out. Please contact support.');

        const confirmRes = await fetch(API_ENDPOINTS.PRODUCT_CONFIRM, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ registrationReference: ref, sessionId }),
        });
        const confirmData = await confirmRes.json();

        if (!confirmRes.ok || confirmData.status !== 'confirmed') {
          throw new Error(confirmData.error || 'Registration could not be confirmed');
        }

        setConfirmed(true);
        setPaymentMessage('Payment successful! You are registered.');
        setActiveStep(stepLabels.length - 1);
        localStorage.removeItem(getStorageKey(ref));
        window.history.replaceState(null, '', `/store/checkout/${slug}`);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Confirmation failed');
        setPaymentMessage('');
      } finally {
        setIsSubmitting(false);
      }
    },
    [slug, stepLabels.length]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const ref = params.get('ref');
    const cancelled = params.get('payment') === 'cancelled';

    if (cancelled) {
      setError('Payment was cancelled. You can try again.');
      setCheckoutStarted(true);
      setActiveStep(product?.product_type === 'scheduled_seats' ? 2 : 1);
      window.history.replaceState(null, '', `/store/checkout/${slug}`);
      return;
    }

    if (sessionId && ref && handledSessionRef.current !== sessionId) {
      handledSessionRef.current = sessionId;
      setCheckoutStarted(true);
      finalizeRegistration(ref, sessionId);
    }
  }, [location.search, finalizeRegistration, slug, product?.product_type]);

  const validateCustomer = () => {
    if (!customer.name.trim()) return 'Name is required';
    if (!customer.email.trim() || !customer.email.includes('@')) return 'Valid email is required';
    const digits = customer.phone.replace(/\D/g, '');
    const is10Digit = customer.phoneCountryCode === '+1' || customer.phoneCountryCode === '+91';
    if (is10Digit && digits.length !== 10) return 'Phone must be 10 digits';
    if (!is10Digit && (digits.length < 4 || digits.length > 15)) return 'Phone must be 4–15 digits';
    return null;
  };

  const handleNext = () => {
    setError(null);
    if (product?.product_type === 'scheduled_seats' && activeStep === 0) {
      if (!selectedOfferingId) { setError('Please select a class date'); return; }
    }
    const detailsStep = product?.product_type === 'scheduled_seats' ? 1 : 0;
    if (activeStep === detailsStep) {
      const validationError = validateCustomer();
      if (validationError) { setError(validationError); return; }
    }
    setActiveStep((s) => s + 1);
  };

  const handlePayment = async () => {
    if (!product || !pricing || isSubmitting) return;
    const validationError = validateCustomer();
    if (validationError) { setError(validationError); return; }

    try {
      setIsSubmitting(true);
      setError(null);
      setPaymentMessage('Redirecting to secure checkout...');

      const registrationReference = generateReference();
      const phone = buildFullPhone(customer.phoneCountryCode, customer.phone);

      localStorage.setItem(
        getStorageKey(registrationReference),
        JSON.stringify({ productSlug: product.slug, offeringId: selectedOfferingId, customer })
      );

      const successUrl = `${window.location.origin}/store/checkout/${product.slug}?ref=${encodeURIComponent(registrationReference)}&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${window.location.origin}/store/checkout/${product.slug}?ref=${encodeURIComponent(registrationReference)}&payment=cancelled`;

      const response = await fetch(API_ENDPOINTS.PRODUCT_CHECKOUT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productSlug: product.slug,
          offeringId: product.product_type === 'scheduled_seats' ? selectedOfferingId : undefined,
          customerName: customer.name.trim(),
          customerEmail: customer.email.trim(),
          customerPhone: phone,
          phoneCountryCode: customer.phoneCountryCode,
          notes: customer.notes.trim(),
          successUrl,
          cancelUrl,
          registrationReference,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.sessionUrl) throw new Error(data.error || 'Unable to start payment');
      window.location.href = data.sessionUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment failed');
      setPaymentMessage('');
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, backgroundColor: BOOKING_THEME.pageBg, minHeight: '100vh' }}>
        <Box sx={{ maxWidth: 720, mx: 'auto' }}>
          <Skeleton variant="text" width={120} height={32} sx={{ mb: 2 }} />
          <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 2, border: `1px solid ${BOOKING_THEME.border}` }}>
            <Skeleton variant="text" width="50%" height={40} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="80%" height={24} sx={{ mb: 3 }} />
            <Skeleton variant="rectangular" height={48} width={160} />
          </Paper>
        </Box>
      </Box>
    );
  }

  if (!product) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, backgroundColor: BOOKING_THEME.pageBg, minHeight: '100vh' }}>
        <Box sx={{ maxWidth: 720, mx: 'auto' }}>
          <Alert severity="error">{error || 'Product not found'}</Alert>
          <Button sx={{ mt: 2 }} onClick={() => navigate('/store')}>Back to store</Button>
        </Box>
      </Box>
    );
  }

  const typeLabel = getProductTypeLabel(product.product_type);
  const detailsStepIndex = product.product_type === 'scheduled_seats' ? 1 : 0;
  const paymentStepIndex = product.product_type === 'scheduled_seats' ? 2 : 1;
  const confirmStepIndex = stepLabels.length - 1;
  const openClasses = product.offerings?.filter((o) => !o.is_full && o.available_seats > 0).length;

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, backgroundColor: BOOKING_THEME.pageBg, minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 720, mx: 'auto' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/store')}
          sx={{ mb: 2, textTransform: 'none', color: BOOKING_THEME.textSecondary }}
        >
          Back to store
        </Button>

        {!checkoutStarted ? (
          /* ── Product detail view ── */
          <Paper
            elevation={0}
            sx={{ p: { xs: 3, md: 4 }, borderRadius: 2, border: `1px solid ${BOOKING_THEME.border}`, backgroundColor: BOOKING_THEME.cardBg }}
          >
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Chip
                label={typeLabel}
                size="small"
                sx={{
                  bgcolor: typeLabel === 'COURSE' ? '#3c3c6e' : '#1e4d1e',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                }}
              />
            </Box>

            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: BOOKING_THEME.textPrimary }}>
              {product.name}
            </Typography>
            {product.description && (
              <Typography variant="body1" sx={{ mb: 3, color: BOOKING_THEME.textSecondary }}>
                {product.description}
              </Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: BOOKING_THEME.textPrimary }}>
                {formatProductPrice(product)}
                <Typography component="span" variant="body2" sx={{ ml: 1, color: BOOKING_THEME.textSecondary }}>
                  + tax
                </Typography>
              </Typography>
              {product.product_type === 'scheduled_seats' && openClasses !== undefined && (
                <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                  {openClasses} upcoming class{openClasses === 1 ? '' : 'es'} available
                </Typography>
              )}
            </Box>

            {product.product_type === 'scheduled_seats' && product.offerings && product.offerings.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Upcoming sessions
                </Typography>
                {product.offerings.map((offering) => (
                  <Paper
                    key={offering.id}
                    variant="outlined"
                    sx={{ p: 2, mb: 1, opacity: offering.is_full ? 0.5 : 1, borderColor: BOOKING_THEME.border }}
                  >
                    <Typography fontWeight={500}>
                      {format(new Date(offering.session_date), 'EEEE, MMMM d, yyyy')}
                    </Typography>
                    {offering.location && (
                      <Typography variant="body2" color="text.secondary">{offering.location}</Typography>
                    )}
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {offering.currency} ${offering.total_amount.toFixed(2)} incl. tax ·{' '}
                      {offering.is_full ? 'Full' : `${offering.available_seats} seats left`}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            )}

            <Button
              variant="contained"
              size="large"
              onClick={() => { setCheckoutStarted(true); setActiveStep(0); }}
              sx={{
                bgcolor: BOOKING_THEME.stepperActive,
                color: '#fff',
                fontWeight: 600,
                px: 4,
                '&:hover': { bgcolor: '#222' },
                borderRadius: 2,
              }}
            >
              Buy now
            </Button>
          </Paper>
        ) : (
          /* ── Checkout flow ── */
          <Paper
            elevation={0}
            sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, border: `1px solid ${BOOKING_THEME.border}`, backgroundColor: BOOKING_THEME.cardBg, maxWidth: 720, mx: 'auto' }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: BOOKING_THEME.textPrimary }}>
              Checkout — {product.name}
            </Typography>

            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                mb: 3,
                '& .MuiStepLabel-label': {
                  fontSize: '0.75rem',
                  color: 'var(--cwy-textSecondary, #4f4f4f)',
                  '&.Mui-active': { color: 'var(--cwy-textPrimary, #0f0f0f)', fontWeight: 600 },
                  '&.Mui-completed': { color: 'var(--cwy-textPrimary, #2e7d32)' },
                },
                '& .MuiStepConnector-line': { borderColor: BOOKING_THEME.border },
              }}
            >
              {stepLabels.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
            )}
            {paymentMessage && (
              <Alert severity="info" sx={{ mb: 2 }}>{paymentMessage}</Alert>
            )}

            {/* Step: Choose Class (scheduled_seats only) */}
            {product.product_type === 'scheduled_seats' && activeStep === 0 && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>Select a class date</Typography>
                {!product.offerings?.length && (
                  <Typography color="text.secondary">No upcoming classes scheduled.</Typography>
                )}
                {product.offerings?.map((offering) => (
                  <Paper
                    key={offering.id}
                    variant="outlined"
                    onClick={() => !offering.is_full && setSelectedOfferingId(offering.id)}
                    sx={{
                      p: 2, mb: 1.5,
                      cursor: offering.is_full ? 'not-allowed' : 'pointer',
                      borderColor: selectedOfferingId === offering.id ? BOOKING_THEME.stepperActive : BOOKING_THEME.border,
                      borderWidth: selectedOfferingId === offering.id ? 2 : 1,
                      opacity: offering.is_full ? 0.5 : 1,
                    }}
                  >
                    <Typography fontWeight={500}>
                      {format(new Date(offering.session_date), 'EEEE, MMMM d, yyyy')}
                    </Typography>
                    {offering.location && (
                      <Typography variant="body2" color="text.secondary">{offering.location}</Typography>
                    )}
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {offering.currency} ${offering.total_amount.toFixed(2)} incl. tax ·{' '}
                      {offering.is_full ? 'Full' : `${offering.available_seats} seats left`}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            )}

            {/* Step: Your Details */}
            {activeStep === detailsStepIndex && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {pricing && (
                  <Alert severity="success">
                    Total: {pricing.currency} ${pricing.total_amount.toFixed(2)} (includes $
                    {pricing.tax_amount.toFixed(2)} tax)
                    {selectedOffering && (
                      <> · Class on {format(new Date(selectedOffering.session_date), 'MMM d, yyyy')}</>
                    )}
                  </Alert>
                )}
                <TextField
                  label="Full name" required fullWidth size="small"
                  value={customer.name}
                  onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root fieldset': { borderColor: BOOKING_THEME.border } }}
                />
                <TextField
                  label="Email" required fullWidth size="small" type="email"
                  value={customer.email}
                  onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root fieldset': { borderColor: BOOKING_THEME.border } }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <FormControl sx={{ minWidth: 130 }}>
                    <TextField
                      select label="Code" size="small"
                      value={customer.phoneCountryCode}
                      onChange={(e) => setCustomer({ ...customer, phoneCountryCode: e.target.value })}
                      sx={{ '& .MuiOutlinedInput-root fieldset': { borderColor: BOOKING_THEME.border } }}
                    >
                      {PHONE_COUNTRY_OPTIONS.map((c) => (
                        <MenuItem key={c.code} value={c.code}>{c.code} {c.name}</MenuItem>
                      ))}
                    </TextField>
                  </FormControl>
                  <TextField
                    label="Phone" required fullWidth size="small"
                    value={customer.phone}
                    onChange={(e) => setCustomer({ ...customer, phone: e.target.value.replace(/\D/g, '').slice(0, 15) })}
                    sx={{ '& .MuiOutlinedInput-root fieldset': { borderColor: BOOKING_THEME.border } }}
                  />
                </Box>
                <TextField
                  label="Notes (optional)" fullWidth size="small" multiline rows={2}
                  value={customer.notes}
                  onChange={(e) => setCustomer({ ...customer, notes: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root fieldset': { borderColor: BOOKING_THEME.border } }}
                />
              </Box>
            )}

            {/* Step: Payment */}
            {activeStep === paymentStepIndex && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                {pricing && (
                  <>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {pricing.currency} ${pricing.total_amount.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      You will be redirected to Stripe to complete payment securely.
                    </Typography>
                  </>
                )}
                <Button
                  variant="contained"
                  size="large"
                  onClick={handlePayment}
                  disabled={isSubmitting}
                  sx={{ bgcolor: BOOKING_THEME.stepperActive, color: '#fff', '&:hover': { bgcolor: '#222' }, borderRadius: 2 }}
                >
                  {isSubmitting ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Pay with Stripe'}
                </Button>
              </Box>
            )}

            {/* Step: Confirmation */}
            {activeStep === confirmStepIndex && confirmed && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="success.main" gutterBottom>
                  You&apos;re all set!
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Thank you, {customer.name}.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  A confirmation will be sent to {customer.email}.
                  {selectedOffering && (
                    <> Your class is on {format(new Date(selectedOffering.session_date), 'MMMM d, yyyy')}.</>
                  )}
                </Typography>
                <Button
                  sx={{ mt: 3 }}
                  onClick={() => navigate('/store')}
                  variant="contained"
                  style={{ backgroundColor: BOOKING_THEME.stepperActive, color: '#fff' }}
                >
                  Back to store
                </Button>
              </Box>
            )}

            {!confirmed && (
              <>
                <Divider sx={{ my: 3, borderColor: BOOKING_THEME.border }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Button
                    startIcon={<ArrowBackIcon />}
                    disabled={activeStep === 0 || isSubmitting}
                    onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
                    sx={{ color: BOOKING_THEME.textSecondary, textTransform: 'none' }}
                  >
                    Back
                  </Button>
                  {activeStep < paymentStepIndex && (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      sx={{ bgcolor: BOOKING_THEME.stepperActive, color: '#fff', '&:hover': { bgcolor: '#222' }, borderRadius: 2 }}
                    >
                      Continue
                    </Button>
                  )}
                </Box>
              </>
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
};
